import React, { useState, useEffect } from 'react';
import { UserProfile } from '../../../types';
import { Loader2, Trash2, Plus, Tag } from 'lucide-react';

interface BlogTag {
    id: number;
    name: string;
    slug: string;
    short_description?: string;
    description?: string;
    image_url?: string;
}

interface Props {
  currentUser: UserProfile;
}

const TagManager: React.FC<Props> = ({ currentUser }) => {
    const [tags, setTags] = useState<BlogTag[]>([]);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const [newTag, setNewTag] = useState({
        name: '',
        short_description: '',
        description: '',
        image_url: ''
    });

    useEffect(() => {
        fetchTags();
    }, []);

    const fetchTags = async () => {
        setLoading(true);
        try {
            const res = await fetch('/api/cms/blog-tags');
            if (res.ok) {
                setTags(await res.json());
            } else {
                setError('Failed to load tags.');
            }
        } catch (_e) {
            setError('An error occurred.');
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setNewTag(prev => ({ ...prev, [name]: value }));
    };

    const handleAddTag = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!newTag.name) return;
        setSubmitting(true);
        setError(null);

        try {
            const res = await fetch('/api/cms/blog-tags', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'x-admin-phone': currentUser.phone },
                body: JSON.stringify(newTag)
            });
            if (res.ok) {
                setNewTag({ name: '', short_description: '', description: '', image_url: '' });
                fetchTags();
            } else {
                const err = await res.json();
                setError(err.error || 'Failed to add tag.');
            }
        } catch (_e) {
            setError('An error occurred.');
        } finally {
            setSubmitting(false);
        }
    };

    const handleDeleteTag = async (id: number) => {
        if (!confirm('Are you sure you want to delete this tag?')) return;

        try {
            const res = await fetch('/api/cms/blog-tags', {
                method: 'DELETE',
                headers: { 'Content-Type': 'application/json', 'x-admin-phone': currentUser.phone },
                body: JSON.stringify({ ids: [id] })
            });
            if (res.ok) {
                fetchTags();
            } else {
                alert('Failed to delete tag.');
            }
        } catch (_e) {
            alert('An error occurred.');
        }
    };

    return (
        <div className="grid grid-cols-12 gap-6">
            <div className="col-span-8">
                <h3 className="text-lg font-bold mb-4">Existing Tags</h3>
                {loading ? <Loader2 className="animate-spin" /> : (
                    <div className="flex flex-wrap gap-2">
                        {tags.map(tag => (
                            <div key={tag.id} className="group flex items-center gap-2 p-2 bg-mystic-bg rounded-full border border-gray-700 hover:border-gray-500">
                                <Tag className="w-4 h-4 text-gray-500" />
                                <span className="font-bold text-white text-sm">{tag.name}</span>
                                <button onClick={() => handleDeleteTag(tag.id)} className="text-gray-600 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity">
                                    <Trash2 className="w-3 h-3" />
                                </button>
                            </div>
                        ))}
                    </div>
                )}
            </div>
            <div className="col-span-4">
                <form onSubmit={handleAddTag} className="bg-mystic-box border border-gray-800 p-6 rounded-lg space-y-4 sticky top-20">
                    <h3 className="text-lg font-bold mb-2">Add New Tag</h3>
                    <input type="text" name="name" value={newTag.name} onChange={handleInputChange} placeholder="Tag Name" required className="w-full bg-mystic-inset border border-gray-700 p-2 rounded text-white" />
                    <textarea name="short_description" value={newTag.short_description} onChange={handleInputChange} placeholder="Short Description (for previews)" className="w-full bg-mystic-inset border border-gray-700 p-2 rounded text-white h-20"></textarea>
                    <textarea name="description" value={newTag.description} onChange={handleInputChange} placeholder="Long Description (full content)" className="w-full bg-mystic-inset border border-gray-700 p-2 rounded text-white h-24"></textarea>
                    <input type="text" name="image_url" value={newTag.image_url} onChange={handleInputChange} placeholder="Image URL" className="w-full bg-mystic-inset border border-gray-700 p-2 rounded text-white" />
                    <button type="submit" disabled={submitting} className="w-full bg-mystic-accent hover:bg-mystic-accentHover text-black font-bold py-2 rounded flex items-center justify-center gap-2 disabled:opacity-50">
                        {submitting ? <Loader2 className="animate-spin" /> : <Plus />}
                        Add Tag
                    </button>
                    {error && <p className="text-red-500 text-xs">{error}</p>}
                </form>
            </div>
        </div>
    );
};

export default TagManager;
